import React from 'react';
import _ from 'lodash';
import { v4 as uuid, } from 'uuid';
import PropTypes from 'prop-types';
import SearchBar from '../../searchbar/SearchBar';
import FieldSet from '../../fieldset/FieldSet';
import Grid from '../../grid/Grid';
import AsyncActionButton from '../../button/AsyncActionButton';
import AutopayService from '../../../service/AutopayService';
import WaterService from '../../../service/WaterService';
import DetailedWaterBill from './DetailedWaterBill';
import BulkAccountLinkForm from '../../form/BulkAccountLinkForm';
import AccountDetailModal from './AccountDetailModal';

class WaterView extends React.Component {
  constructor(props) {
    super(props);
    this.comboFields = [
      {
        value: 'customerId',
        display: 'Customer ID',
      },
      {
        value: 'externalAccountId',
        display: 'External Account ID',
      },
      {
        value: 'bulkId',
        display: 'Bulk ID',
      },
    ];

    this.state = {
      isOpen: false,
      accountCount: 0,
      billCount: 0,
    };
  }

  static accountExclusions = [ 'customerIds', 'clientData', 'serviceOfferingId', 'waterAccountPayments', 'accountBalance', ];

  static billInclusions = [
    'id',
    'isPaid',
    'externalBillId',
    'externalAccountId',
    'billBalance',
    'clientReconciledTimestamp',
    'dueDate',
  ];

  onReset() {
    this.onAccountReset();
    this.onBillReset();
  }

  onAccountReset() {
    this.setState({
      selectedAccount: null,
      accountHeaders: [],
      accountRows: [],
      accountCount: 0,
      billCount: 0,
    });
  }

  onBillReset() {
    this.setState({
      selectedBill: null,
      billHeaders: [],
      billRows: [],
      billCount: 0,
    });
  }

  onSearch(event) {
    const { search, combo, } = event;
    const { serviceOfferingId, } = this.props;
    const criteria = {};
    criteria[combo.value] = search.value;
    criteria.serviceOfferingId = serviceOfferingId;
    let waterAccounts = [];

    this.onReset();
    WaterService.getWaterAccountBySearch(criteria)
      .then((res) => {
        waterAccounts = res.data.accounts;
        return AutopayService.searchAutopayEnrollments({
          serviceOfferingId,
          enrollmentStatus: 'ENROLLED',
        });
      })
      .then(res => {
        if (res.data) {
          const autopayAccounts = res.data.accounts;
          waterAccounts = waterAccounts.map(waterAcc => {
            const isEnrolled = autopayAccounts.some(acc => acc.accountId === waterAcc.id
              && acc.externalAccountId === waterAcc.externalAccountId);
            return { ...waterAcc, autopay: isEnrolled, };
          });
        }
        const model = Grid.toModelFromList(waterAccounts, {
          headerFormatter: (value) => _.startCase(value.header),
          filter: (value) => !WaterView.accountExclusions.includes(value),
        });
        this.setState({
          accountHeaders: model.headers,
          accountRows: model.format(waterAccounts),
          accountCount: waterAccounts.length,
        });
      })
      .finally(() => {
        event.kill();
      });
  }

  onRowSelect(selectedRow) {
    const { accountRows, selectedAccount, } = this.state;
    if (!selectedAccount || selectedRow.id !== selectedAccount.id) {
      this.onBillReset();
      const account = accountRows.find((row) => {
        const { raw, } = row;
        return raw.id === selectedRow.id;
      });

      this.setState({
        selectedAccount: account.raw,
      });
    }
  }

  hideModal = () => {
    this.setState({ isOpen: false, });
  };

  onBillSelect(selectedRow) {
    const { billRows, } = this.state;
    const bill = billRows.find((row) => {
      const { raw, } = row;
      return raw.id === selectedRow.id;
    });

    this.setState({
      selectedBill: bill.raw,
      isOpen: true,
    });
  }

  onServiceOfferingChange() {
    this.onAccountReset();
    this.onBillReset();
  }

  getAccountFooter(selectedAccount) {
    const { isLoading, accountRows, } = this.state;
    const { serviceOfferingId, } = this.props;

    if (!selectedAccount) {
      return null;
    }

    return (
      <div className='container mt-2'>
        <p>{`Selected ID: ${selectedAccount.id}`}</p>
        <hr />
        <div className='row'>
          <div className="m-1">
            <AccountDetailModal selectedAccount={selectedAccount} />
          </div>
          <div className='m-1'>
            <AsyncActionButton
              contents='Get Bills'
              isLoading={isLoading}
              onClick={() => this.getBills({
                serviceOfferingId,
                payitAccountId: selectedAccount.id,
              })}
            />
          </div>
          {selectedAccount.bulkId && (
            <div className="m-1">
              <BulkAccountLinkForm
                customerIds={selectedAccount.customerIds}
                serviceOfferingId={selectedAccount.serviceOfferingId}
                id={selectedAccount.bulkId}
                numberOfAccounts={accountRows.filter(row => {
                  const { raw, } = row;
                  return raw.bulkId === selectedAccount.bulkId;
                }).length}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  getBills(criteria) {
    this.setState({ isLoading: true, });

    return WaterService.getWaterBillsByAccountId(criteria)
      .then((res) => {
        this.onBillReset();

        const { bills, } = res.data;
        const model = Grid.toModelFromList(bills, {
          headerFormatter: (value) => _.startCase(value.header),
          filter: (value) => WaterView.billInclusions.includes(value),
        });

        this.setState({
          billHeaders: model.headers,
          billRows: model.format(bills),
          billCount: bills.length,
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.setState({ isLoading: false, });
      });
  }

  formatOptions(selectedId, serviceOfferings) {
    const isSelected = (value) => value === selectedId;
    return (
      <select
        className='form-select mt-2 me-2'
        onBlur={this.onServiceOfferingChange}
      >
        {serviceOfferings.map((offering) => (
          <option
            key={uuid()}
            selected={isSelected(offering.value)}
            value={offering.value}
          >
            {offering.display}
          </option>
        ))}
      </select>
    );
  }

  render() {
    const {
      accountHeaders,
      accountRows,
      selectedAccount,
      billHeaders,
      billRows,
      selectedBill,
      isOpen,
      accountCount,
      billCount,
    } = this.state;

    return (
      <div className='container'>
        <SearchBar
          comboFields={this.comboFields}
          onClick={(event) => this.onSearch(event)}
        />
        <div className='mt-2'>
          <FieldSet title={`${accountCount} Water Accounts`} style={{ maxHeight: '300px', }}>
            <Grid
              headers={accountHeaders}
              rows={accountRows}
              onRowSelect={(row) => this.onRowSelect(row)}
            />
          </FieldSet>
          {this.getAccountFooter(selectedAccount)}
        </div>
        {billRows && billRows.length > 0 && (
          <div className='mt-2'>
            <FieldSet title={`${billCount} Water Bills`}>
              <Grid
                headers={billHeaders}
                rows={billRows}
                onRowSelect={(row) => this.onBillSelect(row)}
              />
            </FieldSet>
            <DetailedWaterBill
              isOpen={isOpen}
              hideModal={() => this.hideModal()}
              bill={selectedBill}
            />
          </div>
        )}
      </div>
    );
  }
}

WaterView.propTypes = {
  serviceOfferingId: PropTypes.string.isRequired,
};

export default WaterView;
